import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { roundTo } from '@/shared/utils/decimal-scale/decimal-scale.util';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import { useScalarAnnouncer } from '../useScalarAnnouncer/useScalarAnnouncer';
import { useScalarNumber, type UseScalarNumber } from '../useScalarNumber/useScalarNumber';

import { type MoneyInputProps } from './MoneyInput.types';

export type UseMoneyInputLogicInput = Omit<
  MoneyInputProps,
  'id' | 'className' | 'size' | 'aria-describedby'
>;

export interface UseMoneyInputLogic {
  number: UseScalarNumber;
  /** The ISO code beside the box — shown verbatim, never translated. */
  currencyCode: string;
  /** The amount the field is currently about: the draft if there is one. */
  amount: number | undefined;
  /** What the spinbutton reads out — the amount in words, with its per-unit. */
  valueText: string;
  /** What is actually stored, said in words. */
  storedHint: string;
  emptyHint: string;
  rangeHint: string | undefined;
  /** Something wrong with what is in the box **right now**. */
  warningHint: string | undefined;
  /**
   * Where the amount sits inside its slot, as a PHYSICAL side.
   *
   * The box's own base direction is forced LTR (a lone `-` is a bidi-neutral, so
   * an RTL field renders `-3` as `3-`), which means `text-align: start` would
   * resolve against the box rather than against the page and throw the number to
   * the wrong end in Arabic. U7 sanctions exactly one place to convert a logical
   * side into a physical one when the platform underneath understands only
   * physical ones — this is it, computed once, never branched on at a call site.
   */
  amountAlign: 'left' | 'right';
  /** The amount in the box is outside the declared bounds. */
  isOutOfRange: boolean;
  announcement: string;
}

/**
 * An amount of money, edited without losing a decimal on the way in.
 *
 * The kit's `number` field is a controlled `<input type="number">`, and a
 * browser reports an **empty** value for the in-progress `"0."` — so the
 * committed value drops to `undefined`, the controlled prop paints over the
 * keystroke, and `0.075` simply cannot be typed. Every rate on the platform is a
 * decimal, so that is not a rough edge; it is the control being unusable for
 * money. `useScalarNumber` is the family's answer (its own draft text, Arabic
 * digits read as numbers, empty distinct from zero), and this is money's layer
 * on top of it.
 *
 * Two rules it does **not** break, both inherited from the family:
 *
 *  - **Typing is never clamped and never swallowed.** A negative or an
 *    over-large amount is committed and reported in the hint. Validity keeps
 *    exactly one source — the caller's schema — and a control that quietly
 *    holds back a value leaves the form saving something the operator cannot see.
 *  - **Rounding is announced, not silent.** More precision than `decimals` can
 *    hold is stored rounded (there is no smaller unit to offer, as there is for
 *    a duration), so the hint says what will actually be kept.
 */
// `label` is deliberately NOT read here. This control renders exactly one
// input, so the caller's `<Label htmlFor>` names it and an `aria-label` would
// win over the visible label and hide it — the rule `ScalarFieldProps` states
// and `PercentInput` follows for its own box (it consumes `label` only to
// compose a *second* name, for the slider, which this control has no equivalent
// of).
export function useMoneyInputLogic({
  value,
  onValueChange,
  currency,
  perUnit,
  min,
  max,
  decimals,
  step,
  isReadOnly = false,
  isDisabled = false,
}: UseMoneyInputLogicInput): UseMoneyInputLogic {
  const { t } = useTranslation('uiKit');
  const { isRtl, locale } = useDirection();
  const announcer = useScalarAnnouncer();

  // One unit of the last decimal a rate can hold: 0.0001 at four places, 1 at
  // none. An arrow that moves a whole dollar on a per-million-token rate is an
  // arrow nobody presses twice.
  const effectiveStep = step ?? 10 ** -decimals;

  const number = useScalarNumber({
    displayValue: value,
    decimals,
    step: effectiveStep,
    min,
    max,
    isReadOnly,
    isDisabled,
    onCommit: onValueChange,
    onRefuse: announcer.announceReadOnlyRefusal,
  });

  const words = useMemo(
    () =>
      (amount: number): string =>
        formatNumber(amount, locale, {
          style: 'currency',
          currency,
          // The currency's NAME, so the sentence reads "5 US dollars" in English
          // and "٥ دولار أمريكي" in Arabic. A glyph beside Arabic reorders under
          // bidi — P10.4 recorded exactly that for the `$/` in the rate headers.
          currencyDisplay: 'name',
          // `minimumFractionDigits` is left at zero rather than at the currency's
          // own two, so the line states what is STORED — 5, not 5.00 — the same
          // rule the duration hint follows. (It is NOT a crash guard: NumberFormat
          // v3 clamps the minimum under a lower `maximumFractionDigits` rather
          // than throwing, checked on this engine.)
          minimumFractionDigits: 0,
          maximumFractionDigits: decimals,
        }),
    [currency, decimals, locale],
  );

  const withPerUnit = useMemo(
    () =>
      (amount: string): string =>
        perUnit === undefined ? amount : t('money.amountPer', { value: amount, per: perUnit }),
    [perUnit, t],
  );

  const amount = number.draftValue ?? value ?? undefined;

  const emptyHint = t('scalar.empty');

  const storedHint = useMemo(() => {
    if (value === null || value === undefined) return emptyHint;
    // What zero MEANS, not what it prints. An embedding model bills no output
    // tokens, and "0 US dollars per million tokens" is a sentence an operator
    // has to decode; "not charged" is the fact.
    if (value === 0) return t('money.zero');

    return t('money.stored', { value: withPerUnit(words(value)) });
  }, [emptyHint, t, value, withPerUnit, words]);

  const valueText = amount === undefined ? emptyHint : withPerUnit(words(amount));

  const rangeHint = useMemo(() => {
    // A lone minimum of ZERO is the definition of an amount of money, not a rule
    // about this field — and every money field the platform has declares it. In
    // the stills it printed "At least 0 US dollars" under all ten cells of the
    // lab, costing a whole line each at 320. A minimum ABOVE zero, or any
    // maximum, is a real constraint and is always said.
    const floor = min === 0 ? undefined : min;
    if (floor === undefined && max === undefined) return undefined;

    const low = floor === undefined ? undefined : words(floor);
    const high = max === undefined ? undefined : words(max);

    if (low !== undefined && high !== undefined) return t('money.range', { min: low, max: high });
    if (low !== undefined) return t('money.rangeMin', { min: low });

    return t('money.rangeMax', { max: high });
  }, [max, min, t, words]);

  const isOutOfRange =
    amount !== undefined &&
    ((min !== undefined && amount < min) || (max !== undefined && amount > max));

  const warningHint = useMemo(() => {
    // Ordered by how badly the field is wrong: a value that is not a number at
    // all cannot also be out of range, and neither is worth mentioning rounding
    // over.
    if (number.isDraftInvalid) return t('money.notANumber');

    if (isOutOfRange && amount !== undefined) {
      return min !== undefined && amount < min
        ? t('money.belowMin', { min: words(min) })
        : t('money.aboveMax', { max: words(max ?? amount) });
    }

    const draft = number.draftValue;
    if (draft === undefined || roundTo(draft, decimals) === draft) return undefined;

    // The PRECISION only, never the amount. `setText` commits the rounded value,
    // so `storedHint` is already printing exactly what this line would repeat —
    // and two adjacent sentences carrying one number, one past tense and one
    // future, is noise in a control whose whole argument is that the hint says
    // what is stored. `count`, not a formatted string: Arabic resolves six CLDR
    // categories and "2 منازل" is the dual said in the plural (U8).
    return t('money.rounded', { count: decimals });
  }, [
    amount,
    decimals,
    isOutOfRange,
    max,
    min,
    number.draftValue,
    number.isDraftInvalid,
    t,
    words,
  ]);

  return useMemo(
    () => ({
      number,
      currencyCode: currency,
      amount,
      valueText,
      storedHint,
      emptyHint,
      rangeHint,
      warningHint,
      amountAlign: isRtl ? 'right' : 'left',
      isOutOfRange,
      announcement: announcer.announcement,
    }),
    [
      amount,
      announcer.announcement,
      currency,
      emptyHint,
      isOutOfRange,
      isRtl,
      number,
      rangeHint,
      storedHint,
      valueText,
      warningHint,
    ],
  );
}
